import {
  Component,
  h,
  Prop,
  Event,
  EventEmitter,
  Watch,
  State,
  getMode,
  Listen,
  Element
} from '@stencil/core';
import { Labels, translate } from '../../../../utils/translations';
import {
  Country,
  CountrySelectorConfig
} from '../ds-contextual-navigation-country-selector/ds-contextual-navigation-country-selector';
import { User, ProfileNotifications, ExploreSapLink } from '../../ds-contextual-navigation';
import { NotificationBellConfig } from '../ds-header-notification-bell/ds-header-notification-bell';
import { ShoppingCartConfig } from '../ds-header-shopping-cart/ds-header-shopping-cart';
import { Profile } from '../ds-contextual-navigation-profile/ds-contextual-navigation-profile';
import { ContactUsConfig } from './ds-header-mobile-contact-us/ds-header-mobile-contact-us';
import store from '../../../../store/store';
import { ActivityMapRegion, emitClickTrackerEvent } from '../../../../utils/utils';
import clsx from 'clsx';

enum Menu {
  Navigation,
  Profile,
  CountrySelector,
  ContactUs
}

@Component({
  tag: 'ds-contextual-navigation-mobile-masthead',
  styleUrls: {
    default: 'ds-contextual-navigation-mobile-masthead.default.scss',
    light: 'ds-contextual-navigation-mobile-masthead.light.scss'
  },
  shadow: true
})
export class ContextualNavigationMobileMasthead {
  @Prop() mobileHeaderShown: boolean;
  @Prop() profile?: Profile;
  @Prop() loggedInUser?: User;
  @Prop() exploreSapLink?: ExploreSapLink;
  @Prop() hideExploreSapLink: boolean = false;
  @Prop() shoppingCartConfig?: ShoppingCartConfig;
  @Prop() countrySelectorConfig?: CountrySelectorConfig;
  @Prop() contactUsConfig?: ContactUsConfig;
  @Prop() lastVisitedCountry: Country;
  @Prop() notifications?: ProfileNotifications;
  @Prop() notificationBellConfig?: NotificationBellConfig;
  @Prop() disableProfileMenu: boolean = false;
  @Prop() navigationContext?: string[];

  @State() activeMenu: Menu;

  @Event() openButtonClick: EventEmitter;
  @Event() closeButtonClick: EventEmitter;
  // is emitted and bubbles from ds-contextual-navigation-profile
  @Event() loginButtonClick: EventEmitter;
  // is emitted and bubbles from ds-contextual-navigation-profile
  @Event() logoutButtonClick: EventEmitter;
  @Event() countrySelected: EventEmitter;

  private openButton!: HTMLButtonElement;

  @Element() el: HTMLDsContextualNavigationMobileMastheadElement;

  @Watch('mobileHeaderShown')
  toggleBodyScroll() {
    if (!this.mobileHeaderShown) {
      document.body.style.position = null;
      document.body.style.width = null;
      this._closeAll();
    } else {
      this.activeMenu = this.activeMenu ?? Menu.Navigation;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      emitClickTrackerEvent({
        componentName: ActivityMapRegion.Header,
        title: 'Open Mobile Menu',
        url: ''
      });
    }
  }

  @Listen('keydown', { passive: true })
  accessibilityHandler(e: KeyboardEvent) {
    if (e.code == 'Escape' && this.mobileHeaderShown) {
      this._onMastheadClose();
      this.el.shadowRoot.querySelector('ds-header-mobile-navigation').isOpenFocused = true;
      this.openButton?.focus();
    }
  }

  private _onNavigationMenuClicked() {
    if (this.mobileHeaderShown) {
      this.activeMenu = Menu.Navigation;
      emitClickTrackerEvent({
        componentName: ActivityMapRegion.Header,
        title: 'Open Mobile Navigation',
        url: ''
      });
    } else {
      this.openButtonClick.emit();
    }
  }

  private _onProfileMenuClicked() {
    if (!this.mobileHeaderShown) {
      this.openButtonClick.emit();
    }
    this.activeMenu = Menu.Profile;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: 'Open Mobile Profile',
      url: ''
    });
  }

  private _onCountrySelectorClicked() {
    if (!this.mobileHeaderShown) {
      this.openButtonClick.emit();
    }
    this.activeMenu = Menu.CountrySelector;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: 'Open Mobile Country Selector',
      url: ''
    });
  }

  private _onContactUsMenuClicked() {
    if (!this.mobileHeaderShown) {
      this.openButtonClick.emit();
    }
    this.activeMenu = Menu.ContactUs;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: 'Open Mobile Contact Us',
      url: ''
    });
  }

  private _closeAll() {
    this.activeMenu = null;
  }

  private _onMastheadClose() {
    this.closeButtonClick.emit();
    this._closeAll();
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: 'Close Mobile Menu',
      url: ''
    });
  }

  render() {
    const hasNavMenu = !!store.mainLinks?.length;
    const hasSearch = !store.searchConfig?.disabled;
    const hasExploreMenu = !!this.exploreSapLink && !this.hideExploreSapLink;
    const hasProfileMenu = !this.disableProfileMenu;
    const hasCountrySelector = this.countrySelectorConfig?.enabled;
    const hasShoppingCart = this.shoppingCartConfig?.enabled;
    const hasContactUs = this.contactUsConfig?.enabled;
    const hasNotificationBell = this.notificationBellConfig?.enabled;
    return (
      <div
        class={clsx('mobile-header', {
          'mobile-header--hidden': !this.mobileHeaderShown,
          'mobile-header--without-nav': !store.mainLinks?.length && !!store.searchConfig?.disabled
        })}
        data-mode={getMode(this)}
      >
        {(hasNavMenu || hasExploreMenu || hasSearch) && (
          <ds-header-mobile-navigation
            open={this.activeMenu === Menu.Navigation}
            exploreSapLink={this.exploreSapLink}
            hideExploreSapLink={this.hideExploreSapLink}
            onMobileMenuClicked={() => this._onNavigationMenuClicked()}
            navigationContext={this.navigationContext}
            ref={(el) => {
              this.openButton = el
                ?.querySelector('ds-navigation-button')
                ?.shadowRoot?.querySelector('button') as HTMLButtonElement;
            }}
          >
            <slot name='search-title' slot='search-title'></slot>
            <slot name='search-results' slot='search-results'></slot>
          </ds-header-mobile-navigation>
        )}
        {hasShoppingCart && (
          <ds-header-shopping-cart
            url={this.shoppingCartConfig?.url}
            internalLink={!this.shoppingCartConfig?.externalLink}
            isFull={this.shoppingCartConfig?.isFull}
            hasUnreadNotifications={this.shoppingCartConfig?.hasUnreadNotifications}
            mobile
          />
        )}
        {hasContactUs && (
          <ds-header-mobile-contact-us
            open={this.activeMenu === Menu.ContactUs}
            config={this.contactUsConfig}
            onMenuOpenerClick={() => this._onContactUsMenuClicked()}
            onCxsdsHeaderOpenChat={() => this._onMastheadClose()}
          >
            <slot name='contact-us' slot='contact-us'></slot>
          </ds-header-mobile-contact-us>
        )}
        {hasNotificationBell && (
          <ds-header-notification-bell {...this.notificationBellConfig} mobile />
        )}
        {hasProfileMenu && (
          <ds-contextual-navigation-profile
            open={this.activeMenu === Menu.Profile}
            profile={this.profile}
            loggedInUser={this.loggedInUser}
            notifications={this.notifications}
            onMenuOpenerClick={() => this._onProfileMenuClicked()}
            mobile
          >
            <slot name='profile-message' slot='profile-message'></slot>
          </ds-contextual-navigation-profile>
        )}
        {hasCountrySelector && (
          <ds-contextual-navigation-country-selector
            open={this.activeMenu === Menu.CountrySelector}
            regions={this.countrySelectorConfig?.regions}
            searchDisabled={this.countrySelectorConfig?.searchDisabled}
            lastVisitedCountry={this.lastVisitedCountry}
            defaultActiveCountry={this.countrySelectorConfig?.defaultActiveCountry}
            onCountrySelected={(country) => this.countrySelected.emit(country)}
            onMenuOpenerClick={() => this._onCountrySelectorClicked()}
            mobile
          />
        )}
        <div class='divider'></div>
        <ds-action-menu
          class='close-button'
          openerLabel={translate(Labels['Navigation.CloseMenu'])}
          openerTitle={translate(Labels['Navigation.CloseMenu'])}
          onOpenerClick={(e) => {
            this._onMastheadClose();
            if ((e.detail as PointerEvent).pointerType === '') {
              //if clicked by keyboard, then focus back burger open button
              this.el.shadowRoot.querySelector('ds-header-mobile-navigation').isOpenFocused = true;
              this.openButton?.focus();
            }
          }}
        >
          <ds-icon name='Decline' class='close-icon' size='big' slot='opener-content' />
        </ds-action-menu>
      </div>
    );
  }
}
